import React, {
  ComponentType,
  useState
} from 'react'
import {
  Card,
  Container,
  Form,
  Grid,
  Icon,
  Message,
  Segment
} from 'semantic-ui-react'
// Local
import { API_URL, shouldRenderReskin2025 } from '../../constant'
import { api } from '../../action/api'
import If from '../common/if'
import ReveleerCoachingBox from '../ReveleerCoachingBox'
import LockVersionTwoLocked from '../icons/LockVersionTwoLocked'

import { UnregisteredUser } from './unregisteredUser' 

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>
const SafeForm = Form as unknown as ComponentType<any>
const SafeCard = Card as unknown as ComponentType<any>

type ValidationResponse = {
  valid: boolean
  errors?: string[]
}

type UserRegistrationFormProps = {
  user: UnregisteredUser | null
  token: string
  onSave: (user: UnregisteredUser) => void
}

/**
 * Provides a registration page for new users to set password, and update
 * first name and last name
 * @param user Unregistered user entity
 * @param token Registration token
 * @param onSave Callback function for save.
 *        Arguments: user entity
 */
function UserRegistrationForm(props: UserRegistrationFormProps): JSX.Element {
  const {
    user: userProp,
    token,
    onSave
  } = props

  const [
    user,
    setUser
  ] = useState<UnregisteredUser>(() => {
    if (userProp) {
      return {
        id: userProp.id,
        email: userProp.email,
        username: userProp.username,
        firstName: userProp.firstName,
        lastName: userProp.lastName,
        password: '',
        passwordConfirm: ''
      }
    }
    return {
      id: '',
      email: '',
      username: '',
      firstName: '',
      lastName: '',
      password: '',
      passwordConfirm: ''
    }
  })

  const [
    errors,
    setErrors
  ] = useState<string[] | null>(null)

  const validate = (userData: UnregisteredUser): string[] => {
    const validationErrors: string[] = []
    if (!userData.firstName || userData.firstName.length === 0) {
      validationErrors.push('First Name is required.')
    }
    if (!userData.lastName || userData.lastName.length === 0) {
      validationErrors.push('Last Name is required.')
    }
    if (
      !userData.password
      || !userData.passwordConfirm
      || userData.password.length < 7
      || userData.passwordConfirm.length < 7
      || userData.password !== userData.passwordConfirm
    ) {
      validationErrors.push(
        'Password and Password Confirmation must meet password requirements'
      )
    }
    return validationErrors
  }

  const isValid = (userData: UnregisteredUser): boolean => (
    validate(userData).length === 0
  )

  const handleChangeValue = (
    event: React.SyntheticEvent,
    data: {
      name: string
      value: string
    }
  ) => {
    const { name, value } = data
    setUser(prevUser => ({
      ...prevUser,
      [name]: value
    }))
  }

  const handleClickRegister = () => {
    const validationErrors = validate(user)
    if (validationErrors.length === 0) {
      const url = API_URL.UNREGISTERED_USER_VALIDATE(token)
      api.put(
        url,
        user
      ).then(({ data }: { data: ValidationResponse }) => {
        if (data.valid === true) {
          setErrors(null)
          onSave({
            id: user.id,
            email: user.email,
            username: user.username,
            firstName: user.firstName,
            lastName: user.lastName,
            password: user.password,
            passwordConfirm: user.passwordConfirm
          })
        } else {
          setErrors(data.errors || [])
        }
      }).catch(() => {
        // Error handled by api.defaultErrorHandler
        setErrors([ 'An error occurred during registration. Please try again.' ])
      })
    } else {
      setErrors(validationErrors)
    }
  }

  const isSavable = isValid(user)

  return (
    <div>
      <Container>
        <SafeSegment>
          <div className="bold">Welcome to the Curation Health Platform!</div>
          <div className="m-1-top">
            Please verify your first and last name and create a password.
          </div>
        </SafeSegment>
        <If isTrue={ !!errors }>
          <If isTrue={ !shouldRenderReskin2025 }>
            <Message
              error
              header="Registration Error"
              list={ errors || [] }
            />
          </If>
          <If isTrue={ shouldRenderReskin2025 }>
            <ReveleerCoachingBox
              coachingBoxType="error"
              isShowing
              message={
                <p>
                  { errors || [] }
                </p>
              }
            />
          </If>
        </If>
        <SafeForm>
          <SafeGrid>
            <Grid.Row>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              >
                <Form.Group widths="equal">
                  <Form.Input
                    required
                    label="First Name"
                    placeholder="First Name"
                    name="firstName"
                    value={ user.firstName }
                    onChange={ handleChangeValue }
                  />
                  <Form.Input
                    required
                    label="Last Name"
                    placeholder="Last Name"
                    name="lastName"
                    value={ user.lastName }
                    onChange={ handleChangeValue }
                  />
                </Form.Group>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              >
                <SafeGrid
                  columns={ 2 }
                  divided
                  relaxed="very"
                  stackable
                >
                  <Grid.Column>
                    <Form.Input
                      required
                      type="password"
                      label="Password"
                      placeholder="Password"
                      name="password"
                      value={ user.password }
                      onChange={ handleChangeValue }
                    />
                    <Form.Input
                      required
                      type="password"
                      label="Confirm Password"
                      placeholder="Confirm Password"
                      name="passwordConfirm"
                      value={ user.passwordConfirm }
                      onChange={ handleChangeValue }
                    />
                    <If isTrue={ isSavable }>
                      <div className="m-1-top text-align-center">
                        <Form.Button
                          primary
                          onClick={ handleClickRegister }
                        >
                          Register
                        </Form.Button>
                      </div>
                    </If>
                    <If isTrue={ !isSavable }>
                      <div className="m-1-top text-align-center">
                        <span onClick={ handleClickRegister }>
                          <Form.Button disabled>Register</Form.Button>
                        </span>
                      </div>
                    </If>
                  </Grid.Column>
                  <Grid.Column>
                    <SafeCard>
                      <Card.Content>
                        <Card.Header>
                          <span>
                            <If isTrue={ !shouldRenderReskin2025 }>
                              <Icon name="check circle outline" /> Password Requirements
                            </If>
                            <If isTrue={ shouldRenderReskin2025 }>
                              <span className="m-8px-right">
                                <LockVersionTwoLocked />
                              </span>
                              Password Requirements
                            </If>
                          </span>
                        </Card.Header>
                      </Card.Content>
                      <Card.Content>
                        <ul>
                          <li>Your password must be at least seven characters long</li>
                          <li>It must contain 1 each of the following:
                            <ul>
                              <li>Upper case letter</li>
                              <li>Lower case letter</li>
                              <li>Number</li>
                              <li>Special character
                                <div>Examples: ! # $ % ^ & *</div>
                              </li>
                            </ul>
                          </li>
                          <li>
                            Passwords must not be based on something that can be easily guessed or obtained using
                            personal information (e.g. names, favorite sports team, etc.)
                          </li>
                          <li>Passwords must not use four or more sequential or repetitive characters</li>
                          <li>Passwords that have been compromised in a data breach must not be used</li>
                        </ul>
                      </Card.Content>
                    </SafeCard>
                  </Grid.Column>
                </SafeGrid>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              />
            </Grid.Row>
          </SafeGrid>
        </SafeForm>
      </Container>
    </div>
  )
}

export default UserRegistrationForm
